/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'

import { alternateGray, primaryTextColor, primaryYellow } from '../../../../../lib/styles/colors'
import { buttonTextCss, overlineTextCss } from '../../../../../lib/styles/typography'

type CommentCountProps = {
  /** Indicates if this plan is a template */
  isTemplate: boolean
  /** Number of new comments */
  newCommentCount: number
  /** Event callback when the comment count is clicked */
  onCountClick: () => void
  /** Hide the new comment indicator after the comments have been viewed */
  showNewCommentIndicator: boolean
  /** Total number of comments */
  totalCommentCount: number
}

const countContainerCss = css`
  display: flex;
  padding-top: 10px;
`

const showCommentContainerCss = css`
  display: inline-block;
  padding-top: 3px;
`

const totalCountButtonCss = css`
  ${buttonTextCss};
  background: none;
  border: none;
  text-decoration: underline;
  text-transform: none;

  cursor: pointer;
  color: ${primaryTextColor};

  &:focus,
  &:hover {
    color: ${alternateGray};
  }
`

const newCommentCss = css`
  ${overlineTextCss}
  background-color: ${primaryYellow};
  border-radius: 16px;
  color: #000000;
  display: inline-block;
  height: 16px;
  margin-left: 5px;
  margin-top: 5px;
  text-align: center;
  width: 56px;
`

const CommentCount = ({
  isTemplate,
  newCommentCount,
  onCountClick,
  showNewCommentIndicator,
  totalCommentCount
}: CommentCountProps) => {
  const renderNewCount = () => {
    if (isTemplate || !newCommentCount || newCommentCount === 0 || !showNewCommentIndicator) {
      return null
    }
    return <div css={newCommentCss}>{`${newCommentCount} NEW`}</div>
  }

  const renderTotalCount = () => {
    if (!totalCommentCount || totalCommentCount === 0) {
      return null
    }

    return (
      <div css={showCommentContainerCss}>
        <button
          aria-label="Show all comments"
          css={totalCountButtonCss}
          onClick={onCountClick}
          type="button"
        >
          {`${totalCommentCount} ${totalCommentCount === 1 ? 'Comment' : 'Comments'}`}
        </button>
      </div>
    )
  }

  return (
    <div css={countContainerCss}>
      {renderTotalCount()}
      {renderNewCount()}
    </div>
  )
}

export default CommentCount
